"use client"

import React from 'react'
import { Lock } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface ProLockScreenProps {
  componentName?: string
  className?: string
  compact?: boolean
}

export function ProLockScreen({ 
  componentName = "Pro Component",
  className,
  compact = false 
}: ProLockScreenProps) {
  const isDevelopment = process.env.NODE_ENV === 'development'
  
  if (compact) {
    // Compact version for smaller components like sidebar
    return (
      <div className={cn(
        "flex flex-col items-center justify-center p-4 text-center",
        "rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700",
        "bg-gray-50/50 dark:bg-gray-900/50",
        className
      )}>
        <Lock className="h-8 w-8 text-gray-400 dark:text-gray-600 mb-2" />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {isDevelopment ? 'CLI Authentication Required' : 'API Key Required'}
        </p>
        <a 
          href={isDevelopment 
            ? "https://moonui.dev/docs/cli#authentication" 
            : "https://moonui.dev/docs/authentication"}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline font-medium mt-1"
        >
          {isDevelopment ? 'Setup CLI Auth →' : 'Setup API Keys →'}
        </a>
      </div>
    )
  }
  
  // Full version for larger components
  return (
    <div className={cn(
      "flex flex-col items-center justify-center",
      "rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700",
      "bg-gradient-to-b from-gray-50 to-gray-100/50 dark:from-gray-900 dark:to-gray-950/50",
      "p-8 text-center min-h-[200px]",
      className
    )}>
      <div className="relative">
        <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
        <Lock className="relative h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
      </div>
      
      <h3 className="text-lg font-semibold mb-2">{componentName}</h3>
      
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
        {isDevelopment 
          ? 'CLI authentication required to access Pro components'
          : 'API key configuration required for Pro components'}
      </p>
      
      <div className="flex flex-col gap-3">
        {/* Primary action button */}
        <a
          href={isDevelopment 
            ? "https://moonui.dev/docs/cli#authentication"
            : "https://moonui.dev/docs/authentication"}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Lock className="h-3 w-3" />
          {isDevelopment ? 'Setup CLI Authentication' : 'Setup API Keys'}
        </a>
        
        {/* Secondary action */}
        <div className="flex flex-col gap-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {isDevelopment 
              ? 'Run: moonui login'
              : 'Add MOONUI_LICENSE_KEY to .env'}
          </p>
          <a 
            href="https://moonui.dev/pricing" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            View Pro Plans →
          </a>
        </div>
      </div>
    </div>
  )
}

// Export as default as well for easier imports
export default ProLockScreen